import { EditorSelection } from "@codemirror/state";

import { ensureEmptyLine } from "../../toolbar/commands.js";
import { extendBackwardsOverEmptyLines, extendForwardsOverEmptyLines } from "../../selection.js";
import { parseColumnSpecifications } from "../utils.js";
import { TableSelection } from "../contexts/selection-context.js";

export const BorderTheme = {
    NO_BORDERS: 0,
    FULLY_BORDERED: 1,
    BOOKTABS: 2,
};

const themeGenerators = {
    [BorderTheme.NO_BORDERS]: {
        column: () => ({ left: false, right: false }),
        row: () => false,
        multicolumn: () => ({ left: false, right: false }),
    },
    [BorderTheme.FULLY_BORDERED]: {
        column: (number, numColumns) => ({ left: true, right: number === numColumns - 1 }),
        row: () => "\\hline",
        multicolumn: () => ({ left: true, right: true }),
        lastRow: () => "\\hline",
    },
    [BorderTheme.BOOKTABS]: {
        column: () => ({ left: false, right: false }),
        row: (number) => {
            if (number === 0) return "\\toprule";
            if (number === 1) return "\\midrule";
            return false;
        },
        multicolumn: () => ({ left: false, right: false }),
        lastRow: () => "\\bottomrule",
    },
};

const contains = (outer, inner) => outer.from <= inner.from && outer.to >= inner.to;

export const generateColumnSpecification = (columns) =>
    columns
        .map(
            ({ borderLeft, borderRight, content, cellSpacingLeft, cellSpacingRight, customCellDefinition }) =>
                `${"|".repeat(borderLeft)}${cellSpacingLeft}${customCellDefinition}${content}${cellSpacingRight}${"|".repeat(borderRight)}`,
        )
        .join("");

function gobbleEmptyLines(view, pos, lines, target) {
    const line = view.state.doc.lineAt(pos);
    if (line.length !== 0) {
        return { from: pos, to: pos };
    }
    if (target === "above") {
        return {
            from: extendBackwardsOverEmptyLines(view.state.doc, line, lines),
            to: pos,
        };
    }
    return {
        from: pos,
        to: extendForwardsOverEmptyLines(view.state.doc, line, lines),
    };
}

function emptyTable(view, columnSpecification, positions) {
    const newColumns = columnSpecification.slice(0, 1);
    newColumns[0].borderLeft = 0;
    newColumns[0].borderRight = 0;
    view.dispatch({
        changes: [
            {
                from: positions.columnDeclarations.from,
                to: positions.columnDeclarations.to,
                insert: generateColumnSpecification(newColumns),
            },
            {
                from: positions.rowPositions[0].from,
                to: positions.rowPositions[positions.rowPositions.length - 1].to,
                insert: "\\\\",
            },
        ],
    });
}

export function setBorders(view, theme, positions, rowSeparators, table) {
    const generator = themeGenerators[theme];
    const changes = [];
    const spec = view.state.sliceDoc(positions.columnDeclarations.from, positions.columnDeclarations.to);
    const columnSpecification = parseColumnSpecifications(spec);

    columnSpecification.forEach((column, index) => {
        const { left, right } = generator.column(index, columnSpecification.length);
        column.borderLeft = left ? 1 : 0;
        column.borderRight = right ? 1 : 0;
    });

    const nextSpec = generateColumnSpecification(columnSpecification);
    if (nextSpec !== spec) {
        changes.push({
            from: positions.columnDeclarations.from,
            to: positions.columnDeclarations.to,
            insert: nextSpec,
        });
    }

    for (let i = 0; i < positions.rowPositions.length; i += 1) {
        const row = positions.rowPositions[i];
        const topBorder = generator.row(i, positions.rowPositions.length);
        if (topBorder) {
            let borderPresent = false;
            for (const hline of row.hlines) {
                if (hline.from > rowSeparators[i]?.to) continue;
                if (borderPresent) {
                    changes.push({ from: hline.from, to: hline.to, insert: "" });
                } else {
                    const type = view.state.sliceDoc(hline.from, hline.to);
                    if (type.trim() !== topBorder) {
                        changes.push({ from: hline.from, to: hline.to, insert: topBorder });
                    }
                    borderPresent = true;
                }
            }
            if (!borderPresent) {
                changes.push({ from: row.from, to: row.from, insert: topBorder });
            }
        } else {
            for (const hline of row.hlines) {
                if (hline.from > rowSeparators[i]?.to) continue;
                changes.push({ from: hline.from, to: hline.to, insert: "" });
            }
        }
    }

    const lastRow = positions.rowPositions[positions.rowPositions.length - 1];
    const lastRowBorder = generator.lastRow?.();
    const hasLastRowSeparator = positions.rowPositions.length === rowSeparators.length;
    if (hasLastRowSeparator) {
        if (lastRowBorder) {
            let borderPresent = false;
            for (const hline of lastRow.hlines) {
                if (hline.from < rowSeparators[positions.rowPositions.length - 1].to) continue;
                if (borderPresent) {
                    changes.push({ from: hline.from, to: hline.to, insert: "" });
                } else {
                    const type = view.state.sliceDoc(hline.from, hline.to);
                    if (type.trim() !== lastRowBorder) {
                        changes.push({ from: hline.from, to: hline.to, insert: lastRowBorder });
                    }
                    borderPresent = true;
                }
            }
            if (!borderPresent) {
                changes.push({
                    from: rowSeparators[positions.rowPositions.length - 1].to,
                    to: rowSeparators[positions.rowPositions.length - 1].to,
                    insert: ` ${lastRowBorder}`,
                });
            }
        }
    } else if (lastRowBorder) {
        changes.push({ from: lastRow.to, to: lastRow.to, insert: `\\\\ ${lastRowBorder}` });
    }

    for (const row of table.rows) {
        for (const cell of row.cells) {
            if (!cell.multiColumn) continue;
            const { left, right } = generator.multicolumn();
            const currentSpec = view.state.sliceDoc(cell.multiColumn.columns.from, cell.multiColumn.columns.to);
            const columnSpec = parseColumnSpecifications(currentSpec);
            columnSpec.forEach((column) => {
                column.borderLeft = left ? 1 : 0;
                column.borderRight = right ? 1 : 0;
            });
            const updated = generateColumnSpecification(columnSpec);
            if (updated !== currentSpec) {
                changes.push({
                    from: cell.multiColumn.columns.from,
                    to: cell.multiColumn.columns.to,
                    insert: updated,
                });
            }
        }
    }

    view.dispatch({ changes });
}

export function setAlignment(view, selection, alignment, positions, table) {
    if (selection.isMergedCellSelected(table)) {
        if (alignment === "paragraph") return;
        const { minX, minY } = selection.normalized();
        const cell = table.getCell(minY, minX);
        if (!cell.multiColumn) return;
        const specification = view.state.sliceDoc(cell.multiColumn.columns.from, cell.multiColumn.columns.to);
        const columnSpecification = parseColumnSpecifications(specification);
        columnSpecification.forEach((column) => {
            if (column.alignment !== alignment) {
                column.alignment = alignment;
                column.content = alignment[0];
            }
        });
        view.dispatch({
            changes: {
                from: cell.multiColumn.columns.from,
                to: cell.multiColumn.columns.to,
                insert: generateColumnSpecification(columnSpecification),
            },
        });
        return;
    }

    const specification = view.state.sliceDoc(positions.columnDeclarations.from, positions.columnDeclarations.to);
    const columnSpecification = parseColumnSpecifications(specification);
    const { minX, maxX } = selection.normalized();
    for (let i = minX; i <= maxX; i += 1) {
        if (selection.isColumnSelected(i, table)) {
            columnSpecification[i].alignment = alignment;
            columnSpecification[i].content = alignment === "paragraph" ? "p{0.25\\linewidth}" : alignment[0];
            columnSpecification[i].isParagraphColumn = alignment === "paragraph";
        }
    }
    view.dispatch({
        changes: {
            from: positions.columnDeclarations.from,
            to: positions.columnDeclarations.to,
            insert: generateColumnSpecification(columnSpecification),
        },
    });
}

export function removeRowOrColumns(view, selection, positions, cellSeparators, table) {
    const { minX: startCell, maxX: endCell, minY: startRow, maxY: endRow } = selection.normalized();
    const specification = view.state.sliceDoc(positions.columnDeclarations.from, positions.columnDeclarations.to);
    const columnSpecification = parseColumnSpecifications(specification);
    const changes = [];

    if (selection.spansEntireTable(table)) {
        emptyTable(view, columnSpecification, positions);
        return new TableSelection({ cell: 0, row: 0 });
    }

    const removingFromBeginning = selection.isColumnSelected(0, table);
    for (let row = startRow; row <= endRow; row += 1) {
        if (selection.isRowSelected(row, table)) {
            const rowPosition = positions.rowPositions[row];
            changes.push({ from: rowPosition.from, to: rowPosition.to, insert: "" });
        } else {
            for (let cell = startCell; cell <= endCell; ) {
                const cellIndex = table.getCellIndex(row, cell);
                const cellPosition = positions.cells[row][cellIndex];
                if (selection.isColumnSelected(cell, table)) {
                    if (removingFromBeginning) {
                        changes.push({ from: cellPosition.from, to: cellSeparators[row][cellIndex].to, insert: "" });
                    } else {
                        changes.push({
                            from: cellSeparators[row][cellIndex - 1].from,
                            to: cellPosition.to,
                            insert: "",
                        });
                    }
                }
                cell += table.rows[row].cells[cellIndex].multiColumn?.columnSpan ?? 1;
            }
        }
    }

    const filtered = columnSpecification.filter((_, i) => !selection.isColumnSelected(i, table));
    if (filtered.length) {
        changes.push({
            from: positions.columnDeclarations.from,
            to: positions.columnDeclarations.to,
            insert: generateColumnSpecification(filtered),
        });
    }
    view.dispatch({ changes });
    return new TableSelection({
        cell: Math.max(0, Math.min(filtered.length - 1, startCell)),
        row: Math.max(0, Math.min(positions.rowPositions.length - 1, startRow)),
    });
}

export function insertRow(view, selection, positions, below, rowSeparators, table) {
    const { maxY, minY } = selection.normalized();
    const rowsToInsert = selection.height();
    const from = below ? positions.rowPositions[maxY].to : positions.rowPositions[minY].from;
    const borderTheme = table.getBorderTheme?.();
    const border = borderTheme === BorderTheme.FULLY_BORDERED ? "\\hline" : "";
    const initialRowSeparator = below && rowSeparators.length === table.rows.length - 1 ? "\\\\" : "";
    const insert = `${initialRowSeparator}\n${" &".repeat(table.columns.length - 1)}\\\\${border}`.repeat(rowsToInsert);
    view.dispatch({ changes: { from, to: from, insert } });
    if (!below) return selection;
    return new TableSelection({ cell: 0, row: maxY + 1 }, { cell: table.columns.length - 1, row: maxY + 1 });
}

export function insertColumn(view, initialSelection, positions, after, table) {
    const selection = initialSelection.explode(table);
    const { maxX, minX } = selection.normalized();
    const columnsToInsert = selection.maximumCellWidth(table);
    const changes = [];
    const targetColumn = after ? maxX : minX;

    for (let row = 0; row < positions.rowPositions.length; row += 1) {
        const cell = table.getCell(row, targetColumn);
        const target = cell.multiColumn ?? cell;
        changes.push({
            from: after ? target.to : target.from,
            insert: " &".repeat(columnsToInsert),
        });
    }

    const specification = view.state.sliceDoc(positions.columnDeclarations.from, positions.columnDeclarations.to);
    const columnSpecification = parseColumnSpecifications(specification);
    const targetIndex = after ? maxX + 1 : minX;
    columnSpecification.splice(
        targetIndex,
        0,
        ...Array.from({ length: columnsToInsert }, () => ({
            alignment: "left",
            borderLeft: 0,
            borderRight: 0,
            content: "l",
            cellSpacingLeft: "",
            cellSpacingRight: "",
            customCellDefinition: "",
            isParagraphColumn: false,
        })),
    );
    changes.push({
        from: positions.columnDeclarations.from,
        to: positions.columnDeclarations.to,
        insert: generateColumnSpecification(columnSpecification),
    });
    view.dispatch({ changes });
    if (!after) return selection;
    return new TableSelection({ cell: maxX + 1, row: 0 }, { cell: maxX + 1, row: positions.rowPositions.length - 1 });
}

export function removeNodes(view, ...nodes) {
    view.dispatch({
        changes: nodes.filter(Boolean).map((node) => ({ from: node.from, to: node.to, insert: "" })),
    });
}

export function moveCaption(view, positions, target, tableEnvironment) {
    const changes = [];
    const position = target === "above" ? positions.tabular.from : positions.tabular.to;
    const cursor = EditorSelection.cursor(position);

    if (tableEnvironment?.caption) {
        const existingCaption = tableEnvironment.caption;
        if (
            (existingCaption.from < positions.tabular.from && target === "above") ||
            (existingCaption.from > positions.tabular.to && target === "below")
        ) {
            return;
        }
    }

    const { pos, prefix, suffix } = ensureEmptyLine(view.state, cursor, target);
    if (!tableEnvironment?.caption) {
        let labelText = "\\label{tab:my_table}";
        if (tableEnvironment?.label) {
            changes.push({ from: tableEnvironment.label.from, to: tableEnvironment.label.to, insert: "" });
            labelText = view.state.sliceDoc(tableEnvironment.label.from, tableEnvironment.label.to);
        }
        changes.push({
            ...gobbleEmptyLines(view, pos, 2, target),
            insert: `${prefix}\\caption{Caption}\n${labelText}${suffix}`,
        });
    } else {
        const existingCaption = tableEnvironment.caption;
        let currentCaption = view.state.sliceDoc(existingCaption.from, existingCaption.to);
        if (tableEnvironment.label && !contains(existingCaption, tableEnvironment.label)) {
            currentCaption += `\n${view.state.sliceDoc(tableEnvironment.label.from, tableEnvironment.label.to)}`;
            changes.push({ from: tableEnvironment.label.from, to: tableEnvironment.label.to, insert: "" });
        }
        changes.push({ ...gobbleEmptyLines(view, pos, 2, target), insert: `${prefix}${currentCaption}${suffix}` });
        changes.push({ from: existingCaption.from, to: existingCaption.to, insert: "" });
    }
    view.dispatch({ changes });
}

export function removeCaption(view, tableEnvironment) {
    if (
        tableEnvironment?.caption &&
        tableEnvironment.label &&
        contains(tableEnvironment.caption, tableEnvironment.label)
    ) {
        return removeNodes(view, tableEnvironment.caption);
    }
    return removeNodes(view, tableEnvironment?.caption, tableEnvironment?.label);
}

export function unmergeCells(view, selection, table) {
    const cell = table.getCell(selection.from.row, selection.from.cell);
    if (!cell.multiColumn) return;
    view.dispatch({
        changes: [
            { from: cell.multiColumn.preamble.from, to: cell.multiColumn.preamble.to, insert: "" },
            {
                from: cell.multiColumn.postamble.from,
                to: cell.multiColumn.postamble.to,
                insert: "&".repeat(cell.multiColumn.columnSpan - 1),
            },
        ],
    });
}

export function mergeCells(view, selection, table) {
    const { minX, maxX, minY, maxY } = selection.normalized();
    if (minY !== maxY || minX === maxX) return;
    const content = Array.from({ length: maxX - minX + 1 }, (_, i) => table.getCell(minY, minX + i).content.trim())
        .join(" ")
        .trim();
    const border = table.getBorderTheme?.() === BorderTheme.FULLY_BORDERED ? "|" : "";
    const preamble = `\\multicolumn{${maxX - minX + 1}}{${border}c${border}}{`;
    const { from } = table.getCell(minY, minX);
    const { to } = table.getCell(minY, maxX);
    view.dispatch({ changes: { from, to, insert: `${preamble}${content}}` } });
}

const COMMAND_FOR_PARAGRAPH_ALIGNMENT = {
    left: "\\raggedright",
    right: "\\raggedleft",
    center: "\\centering",
    paragraph: "",
};

function generateParagraphColumnSpecification(alignment) {
    if (alignment === "paragraph") {
        return "";
    }
    return `>{${COMMAND_FOR_PARAGRAPH_ALIGNMENT[alignment]}\\arraybackslash}`;
}

function getParagraphAlignmentCharacter(column) {
    if (!column.isParagraphColumn) {
        return "p";
    }
    const currentAlignmentCharacter = column.content[0];
    if (currentAlignmentCharacter === "m" || currentAlignmentCharacter === "b") {
        return currentAlignmentCharacter;
    }
    return "p";
}

function transformColumnWidth(width) {
    if (width.unit === "%") {
        return width.width / 100;
    }
    return width.width;
}

function getSuffixForUnit(unit) {
    if (unit === "custom") {
        return "";
    }
    if (unit === "%") {
        return "\\linewidth";
    }
    return unit;
}

export function setColumnWidth(view, selection, newWidth, positions, table) {
    const { minX, maxX } = selection.normalized();
    const specification = view.state.sliceDoc(positions.columnDeclarations.from, positions.columnDeclarations.to);
    const columnSpecification = parseColumnSpecifications(specification);
    for (let i = minX; i <= maxX; i += 1) {
        if (!selection.isColumnSelected(i, table)) {
            continue;
        }
        const suffix = getSuffixForUnit(newWidth.unit);
        const widthValue = transformColumnWidth(newWidth);
        columnSpecification[i].customCellDefinition = generateParagraphColumnSpecification(
            columnSpecification[i].alignment,
        );
        const alignmentCharacter = getParagraphAlignmentCharacter(columnSpecification[i]);
        columnSpecification[i].content =
            newWidth.unit === "custom"
                ? `${alignmentCharacter}{${newWidth.width}}`
                : `${alignmentCharacter}{${widthValue}${suffix}}`;
        columnSpecification[i].isParagraphColumn = true;
        if (columnSpecification[i].alignment !== "paragraph" && !columnSpecification[i].customCellDefinition) {
            columnSpecification[i].customCellDefinition = generateParagraphColumnSpecification(
                columnSpecification[i].alignment,
            );
        }
    }
    view.dispatch({
        changes: {
            from: positions.columnDeclarations.from,
            to: positions.columnDeclarations.to,
            insert: generateColumnSpecification(columnSpecification),
        },
    });
}

export function removeColumnWidths(view, selection, positions, table) {
    const { minX, maxX } = selection.normalized();
    const specification = view.state.sliceDoc(positions.columnDeclarations.from, positions.columnDeclarations.to);
    const columnSpecification = parseColumnSpecifications(specification);
    for (let i = minX; i <= maxX; i += 1) {
        if (!selection.isColumnSelected(i, table)) {
            continue;
        }
        columnSpecification[i].customCellDefinition = "";
        columnSpecification[i].isParagraphColumn = false;
        if (columnSpecification[i].alignment === "paragraph") {
            columnSpecification[i].content = "l";
            columnSpecification[i].alignment = "left";
        } else {
            columnSpecification[i].content = columnSpecification[i].alignment[0];
        }
    }
    view.dispatch({
        changes: {
            from: positions.columnDeclarations.from,
            to: positions.columnDeclarations.to,
            insert: generateColumnSpecification(columnSpecification),
        },
    });
}
